/**
 * @fileoverview Reusable field-level validators for inline-editable department forms.
 * @module system-web/utils/fieldValidation
 *
 * Responsibilities:
 * - Provide pure validation functions for ZIP, URL/website, and other
 *   department-level input fields used with InlineEditableField configs.
 * - Return a human-readable error string on failure, or null on success.
 *
 * Dependencies:
 * - None (pure functions, zero external dependencies).
 *
 * Architecture:
 * - Shared utility consumed by DepartmentPanel and any future surfaces
 *   that edit department/tenant records via inline editing.
 * - Validators are stateless and allocation-free on the hot path (regex
 *   literals are module-scoped constants).
 *
 * Notes:
 * - All validators treat empty strings as valid (fields are optional).
 *   Use a separate "required" validator if the field is mandatory.
 * - ZIP validation covers US 5-digit and 5+4 (ZIP+4) formats.
 * - URL validation requires http(s):// scheme.
 */

// ---------------------------------------------------------------------------
// ZIP Code
// ---------------------------------------------------------------------------

/**
 * @security
 *
 * Validation:
 * - US ZIP: exactly 5 digits, optionally followed by a hyphen and 4 digits.
 *
 * Trust Boundary:
 * - Untrusted user input from inline edit fields.
 */
const ZIP_RE = /^\d{5}(-\d{4})?$/;

/**
 * Validate a US ZIP code (5-digit or ZIP+4 format).
 * Returns null if valid or empty, error string otherwise.
 */
export function validateZip(val: unknown): string | null {
  const s = String(val ?? '').trim();
  if (s.length === 0) return null;
  return ZIP_RE.test(s) ? null : 'Enter a valid ZIP (e.g. 95603 or 95603-1234)';
}

// ---------------------------------------------------------------------------
// Website / URL
// ---------------------------------------------------------------------------

/**
 * @security
 *
 * Validation:
 * - Must start with http:// or https:// and contain a valid domain segment.
 *
 * Trust Boundary:
 * - Untrusted user input from inline edit fields.
 */
const URL_RE = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;

/**
 * Validate a website URL (must include http(s):// scheme).
 * Returns null if valid or empty, error string otherwise.
 */
export function validateWebsite(val: unknown): string | null {
  const s = String(val ?? '').trim();
  if (s.length === 0) return null;
  return URL_RE.test(s) ? null : 'Enter a valid URL (e.g. https://example.gov)';
}
